import { ColDef } from 'ag-grid-community';

import { TranslateService } from '@ngx-translate/core';

import { NomenclatureService } from 'src/app/core/services/nomenclature.service';
import { getNestedProperty } from 'src/app/shared/utils/object-utils';

export interface NomenclatureColumnDefOptions {
  headerNameKey: string; // e.g., 'grid.column.query-type'
  fieldKey: string; // e.g., 'object_type.id'
  fieldValue: string; // e.g., 'query_type.name'
  nomenclatureName: string;
  /**
   * Nomenclature property used as the set-filter key (default 'id').
   * E.g. 'alias' for the remediation rule columns, whose backend filter
   * matches rule_code aliases, not ids. Non-'id' keys fetch the raw
   * (untransformed) nomenclature records to reach the property. Sugar over
   * `rawRecord`.
   */
  keyProperty?: string;
  /**
   * Maps a RAW nomenclature record into the {id, title} filter entry - id is
   * the filterModel key, title the dropdown label. Implies a raw
   * (untransformed, uncached) fetch; use when the entry needs more than the
   * default {id, name} - e.g. the curl-code column labels entries
   * "<code> - <name>" so the raw code stays visible/searchable.
   */
  rawRecord?: (record: any) => { id: unknown; title: string };
  /**
   * Keys of the nomenclature entries the set filter offers; everything else is
   * dropped from the dropdown (the filter model is untouched, so a preset
   * holding another key still applies, it just shows no tick). Use when only
   * a subset of a shared nomenclature applies to the grid - e.g. the object
   * classes a schedule can be attached to (WAP-1657). Compared as strings,
   * like the filter keys.
   */
  includeIds?: ReadonlyArray<unknown>;
  otherFilterParams?: any;
}

/**
 * Set-filter column over a backend nomenclature: the dropdown lists the full
 * nomenclature (cached per session, opens instantly - no aggregations
 * round-trip) and the filterModel carries the keys, which the backend grid
 * driver handles centrally. Every grid (app-data-grid hosts and standalone
 * panels alike) calls this directly with its injected services.
 */
export function buildNomenclatureColumnDef<TData = any>(
  translateService: TranslateService,
  nomenclatureService: NomenclatureService,
  {
    headerNameKey,
    fieldKey,
    fieldValue,
    nomenclatureName,
    keyProperty,
    rawRecord,
    includeIds,
    otherFilterParams,
  }: NomenclatureColumnDefOptions,
): ColDef<TData> {
  const colDef: ColDef<TData> = {
    headerName: translateService.instant(headerNameKey),
    // Dotted path string; ColDefField's typed key union can't carry it once
    // TData is generic.
    field: fieldKey as ColDef<TData>['field'],
    filter: 'agSetColumnFilter',

    // Display only the title in the grid cell (assuming the nomenclature object has the properties "id" and "title").
    valueGetter: (params) => {
      return getNestedProperty(params.data, fieldValue, '');
    },

    // Format the value for grid cell display.
    valueFormatter: (params) => params.value,

    filterParams: {
      ...otherFilterParams,
      // * Extract the key (the nomenclature id) for filtering. MUST return a
      // * string: ag-grid keys its filter-list map by this value, while a
      // * programmatic setFilterModel() carries string values (the grid-mapping
      // * utils stringify). A numeric key here misses that lookup, so a
      // * store-pushed filter - a data-view widget drill-down - resolves to
      // * nothing and the column reads as unfiltered.
      keyCreator: (params: any) => String(params.value?.id ?? ''),
      // Format the nomenclature filter values to display title in the dropdown.
      valueFormatter: (params: any) => {
        return params.value ? params.value.title : ''; // nomenclature structure
      },
      values: (params: any) => {
        const allowed = includeIds?.map(String);
        const keep = (entries: any[]) =>
          allowed
            ? entries.filter((entry) => allowed.includes(String(entry.id)))
            : entries;
        const mapRaw =
          rawRecord ??
          (keyProperty && keyProperty !== 'id'
            ? (item: any) => ({ id: item[keyProperty], title: item.name })
            : null);
        if (!mapRaw) {
          nomenclatureService
            .fetchNomenclatureRecords(nomenclatureName)
            .subscribe((response: any[]) => {
              // An array of objects with properties id and title.
              params.success(keep(response));
            });
          return;
        }
        // The transformed records carry only {id, title}, so fetch raw and
        // map into the {id, title} slots the keyCreator/valueFormatter above
        // expect. Raw fetches are not cached by the service - keep such
        // nomenclatures small.
        nomenclatureService
          .fetchNomenclatureRecords(nomenclatureName, false)
          .subscribe((response: { [key: string]: any }) => {
            params.success(keep(Object.values(response).map(mapRaw)));
          });
      },
    },
  };

  return colDef;
}
